import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixEventsNotificationsV2NotificationConfig from './events-notifications-v2-notification-config-notifications.http.js';

export interface NotificationConfig {
  /**
   * Event ID.
   * @format GUID
   * @immutable
   */
  _id?: string | null;
  /**
   * Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision.
   * @immutable
   */
  revision?: string | null;
  /**
   * Represents the time this NotificationConfig was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Represents the time this NotificationConfig was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /** Rsvp confirmation. */
  rsvpConfirmation?: EmailNotificationConfig;
  /** New spots available. */
  newSpotsAvailable?: EmailNotificationConfig;
  /** Event cancelation. */
  eventCancelation?: EmailNotificationConfig;
  /** Event reminder. */
  upcomingEventReminder?: EmailNotificationConfig;
  /** Order confirmation. */
  orderConfirmation?: EmailNotificationConfig;
  /** Ticket confirmation. */
  ticketConfirmation?: EmailNotificationConfig;
  /** Invoice. */
  invoice?: EmailNotificationConfig;
  /** Order confirmation with tickets link. */
  orderConfirmationWithTicketsLink?: EmailNotificationConfig;
}

export interface EmailNotificationConfig {
  /**
   * Predefined id of the notification type.
   * @readonly
   */
  notificationType?: EmailNotificationTypeWithLiterals;
  /** Email template values. */
  template?: EmailTemplate;
  /**
   * Desired timing of the notification in hours respectfully to event start time.
   * For example if value is 0 then notification is sent at the start of the event.
   */
  reminderTime?: EmailNotificationReminderTimeWithLiterals;
  /** When true the notification will be sent when certain trigger occurs. */
  sendingEnabled?: boolean | null;
}

export enum EmailNotificationType {
  /** Triggered when site user RSVP to the event. */
  EMAIL_RSVP_CONFIRMATION = 'EMAIL_RSVP_CONFIRMATION',
  /** Triggered for waiting guests when additional RSVP spots appears in the fully booked event. */
  EMAIL_NEW_SPOTS_AVAILABLE = 'EMAIL_NEW_SPOTS_AVAILABLE',
  /** Triggered when event is canceled. */
  EMAIL_EVENT_CANCELATION = 'EMAIL_EVENT_CANCELATION',
  /** Triggered before the event start time. */
  EMAIL_UPCOMING_EVENT_REMINDER = 'EMAIL_UPCOMING_EVENT_REMINDER',
  /** Triggered when site user creates Order. */
  EMAIL_ORDER_CONFIRMATION = 'EMAIL_ORDER_CONFIRMATION',
  /** Triggered when resending ticket to ticket holder. */
  EMAIL_TICKET_CONFIRMATION = 'EMAIL_TICKET_CONFIRMATION',
  /** Triggered when sending invoice. Currently only `enabled` flag update is supported. */
  EMAIL_INVOICE = 'EMAIL_INVOICE',
}

/** @enumType */
export type EmailNotificationTypeWithLiterals =
  | EmailNotificationType
  | 'EMAIL_RSVP_CONFIRMATION'
  | 'EMAIL_NEW_SPOTS_AVAILABLE'
  | 'EMAIL_EVENT_CANCELATION'
  | 'EMAIL_UPCOMING_EVENT_REMINDER'
  | 'EMAIL_ORDER_CONFIRMATION'
  | 'EMAIL_TICKET_CONFIRMATION'
  | 'EMAIL_INVOICE';

export interface EmailTemplate {
  /**
   * Email subject.
   * @minLength 1
   * @maxLength 100
   */
  subject?: string | null;
  /**
   * Plain text message body.
   * @minLength 1
   * @maxLength 1000
   */
  body?: string | null;
  /** Description link visibility and display text */
  descriptionLink?: Link;
  /** Calendar link visibility and display text */
  calendarLink?: Link;
  /** Online conferencing link visibility and display text */
  onlineConferencingLink?: Link;
  /** When true the tickets are attached to the order confirmation email. */
  includeTickets?: boolean | null;
}

export interface Link {
  /** When true the link is added to the email. */
  display?: boolean | null;
  /**
   * Text of the link.
   * @maxLength 60
   */
  linkText?: string | null;
}

export enum EmailNotificationReminderTime {
  /** Notification is sent instantly */
  INSTANT = 'INSTANT',
  /** 24 hours prior */
  BEFORE_1_DAY = 'BEFORE_1_DAY',
  /** 3 days prior */
  BEFORE_3_DAYS = 'BEFORE_3_DAYS',
  /** 7 days prior */
  BEFORE_1_WEEK = 'BEFORE_1_WEEK',
  /** 1 hour prior */
  BEFORE_1_HOUR = 'BEFORE_1_HOUR',
  /** 30 minutes prior */
  BEFORE_30_MINUTES = 'BEFORE_30_MINUTES',
}

/** @enumType */
export type EmailNotificationReminderTimeWithLiterals =
  | EmailNotificationReminderTime
  | 'INSTANT'
  | 'BEFORE_1_DAY'
  | 'BEFORE_3_DAYS'
  | 'BEFORE_1_WEEK'
  | 'BEFORE_1_HOUR'
  | 'BEFORE_30_MINUTES';

export interface ResolveNotificationConfigRequest {
  /**
   * Id of the NotificationConfig to retrieve
   * @format GUID
   */
  notificationConfigId: string;
}

export interface ResolveNotificationConfigResponse {
  /** The retrieved NotificationConfig */
  notificationConfig?: NotificationConfig;
}

export interface UpsertNotificationConfigRequest {
  /** NotificationConfig to be upserted */
  notificationConfig: NotificationConfig;
}

export interface UpsertNotificationConfigResponse {
  /** The upserted NotificationConfig */
  notificationConfig?: NotificationConfig;
}

export interface ResolveEmailNotificationConfigRequest {
  /**
   * Id of the NotificationConfig to retrieve
   * @format GUID
   */
  notificationConfigId?: string;
  /** Notification type */
  notificationType?: EmailNotificationTypeWithLiterals;
}

export interface ResolveEmailNotificationConfigResponse {
  /** The retrieved EmailNotificationConfig */
  emailNotificationConfig?: EmailNotificationConfig;
}

export interface EventCanceled {
  /** Event canceled timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /** Event title */
  title?: string;
  /**
   * Event creator user ID.
   * @format GUID
   */
  userId?: string | null;
  /** True if at least one guest is registered to the event with any attendance status. */
  hasGuests?: boolean | null;
}

export interface Empty {}

export interface EventDeleted {
  /** Event deleted timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /** Event title. */
  title?: string;
  /**
   * Event creator user ID.
   * @format GUID
   */
  userId?: string | null;
}

export interface EventCopied {
  /** Event created timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /** Event location. */
  location?: Location;
  /** Event schedule configuration. */
  scheduleConfig?: ScheduleConfig;
  /** Event title. */
  title?: string;
  /**
   * Event creator user ID.
   * @format GUID
   */
  userId?: string | null;
  /** Event status. */
  status?: EventStatusWithLiterals;
  /**
   * Instance ID. Indicates the original app instance which current event was derived from.
   * @format GUID
   */
  derivedFromInstanceId?: string | null;
  /**
   * Event ID. Indicates the original event which current event was derived from.
   * @format GUID
   */
  derivedFromEventId?: string | null;
  /**
   * Map of copied ticket definitions from original event.
   * Key represents ticket def id in the original event.
   * Value represents ticket def id in the newly created event.
   */
  ticketDefinitions?: Record<string, string>;
}

export interface Location {
  /**
   * Location name.
   * @maxLength 50
   */
  name?: string | null;
  /** Location map coordinates. */
  coordinates?: MapCoordinates;
  /**
   * Single line address representation.
   * @maxLength 300
   */
  address?: string | null;
  /** Location type. */
  type?: LocationTypeWithLiterals;
  /**
   * Full address derived from formatted single line `address`.
   * When `full_address` is used to create or update the event, deprecated `address` and `coordinates` are ignored.
   * If provided `full_address` has empty `formatted_address` or `coordinates`, it will be auto-completed using Atlas service.
   *
   * Migration notes:
   * - `full_address.formatted_address` is equivalent to `address`.
   * - `full_address.geocode` is equivalent to `coordinates`.
   */
  fullAddress?: Address;
  /**
   * Defines event location as TBD (To Be Determined).
   * When event location is not yet defined, `name` is displayed instead of location address.
   * `coordinates`, `address`, `type` and `full_address` are not required when location is TBD.
   */
  tbd?: boolean | null;
}

export interface MapCoordinates {
  /**
   * Latitude.
   * @min -90
   * @max 90
   */
  lat?: number;
  /**
   * Longitude.
   * @min -180
   * @max 180
   */
  lng?: number;
}

export enum LocationType {
  VENUE = 'VENUE',
  ONLINE = 'ONLINE',
}

/** @enumType */
export type LocationTypeWithLiterals = LocationType | 'VENUE' | 'ONLINE';

/** Physical address */
export interface Address extends AddressStreetOneOf {
  /** a break down of the street to number and street name */
  streetAddress?: StreetAddress;
  /** Main address line (usually street and number) as free text */
  addressLine1?: string | null;
  /**
   * country code
   * @format COUNTRY
   */
  country?: string | null;
  /** subdivision (usually state or region) code according to ISO 3166-2 */
  subdivision?: string | null;
  /** city name */
  city?: string | null;
  /** zip/postal code */
  postalCode?: string | null;
  /** Free text providing more detailed address info. Usually contains Apt, Suite, Floor */
  addressLine2?: string | null;
  /** A string containing the human-readable address of this location */
  formatted?: string | null;
  /** Free text for human-to-human textual orientation aid purposes */
  hint?: string | null;
  /** coordinates of the physical address */
  location?: AddressLocation;
  /** country full-name */
  countryFullname?: string | null;
  /**
   * multi-level subdivisions from top to bottom
   * @maxSize 6
   */
  subdivisions?: Subdivision[];
}

/** @oneof */
export interface AddressStreetOneOf {
  /** a break down of the street to number and street name */
  streetAddress?: StreetAddress;
  /** Main address line (usually street and number) as free text */
  addressLine?: string | null;
}

export interface StreetAddress {
  /** street number */
  number?: string;
  /** street name */
  name?: string;
}

export interface AddressLocation {
  /**
   * address latitude coordinates
   * @min -90
   * @max 90
   */
  latitude?: number | null;
  /**
   * address longitude coordinates
   * @min -180
   * @max 180
   */
  longitude?: number | null;
}

export interface Subdivision {
  /** subdivision short code */
  code?: string;
  /** subdivision full-name */
  name?: string;
}

export enum SubdivisionType {
  UNKNOWN_SUBDIVISION_TYPE = 'UNKNOWN_SUBDIVISION_TYPE',
  /** State */
  ADMINISTRATIVE_AREA_LEVEL_1 = 'ADMINISTRATIVE_AREA_LEVEL_1',
  /** County */
  ADMINISTRATIVE_AREA_LEVEL_2 = 'ADMINISTRATIVE_AREA_LEVEL_2',
  /** City/town */
  ADMINISTRATIVE_AREA_LEVEL_3 = 'ADMINISTRATIVE_AREA_LEVEL_3',
  /** Neighborhood/quarter */
  ADMINISTRATIVE_AREA_LEVEL_4 = 'ADMINISTRATIVE_AREA_LEVEL_4',
  /** Street/block */
  ADMINISTRATIVE_AREA_LEVEL_5 = 'ADMINISTRATIVE_AREA_LEVEL_5',
  /** ADMINISTRATIVE_AREA_LEVEL_0. Indicates the national political entity, and is typically the highest order type returned by the Geocoder. */
  COUNTRY = 'COUNTRY',
}

/** @enumType */
export type SubdivisionTypeWithLiterals =
  | SubdivisionType
  | 'UNKNOWN_SUBDIVISION_TYPE'
  | 'ADMINISTRATIVE_AREA_LEVEL_1'
  | 'ADMINISTRATIVE_AREA_LEVEL_2'
  | 'ADMINISTRATIVE_AREA_LEVEL_3'
  | 'ADMINISTRATIVE_AREA_LEVEL_4'
  | 'ADMINISTRATIVE_AREA_LEVEL_5'
  | 'COUNTRY';

export interface ScheduleConfig {
  /**
   * Defines event as TBD (To Be Determined) schedule.
   * When event time is not yet defined, TBD message is displayed instead of event start and end times.
   * `startDate`, `endDate` and `timeZoneId` are not required when schedule is TBD.
   */
  scheduleTbd?: boolean;
  /**
   * TBD message.
   * @maxLength 100
   */
  scheduleTbdMessage?: string | null;
  /** Event start timestamp. */
  startDate?: Date | null;
  /** Event end timestamp. */
  endDate?: Date | null;
  /**
   * Event time zone ID in TZ database format, e.g., `EST`, `America/Los_Angeles`.
   * @maxLength 100
   */
  timeZoneId?: string | null;
  /** Whether end date is hidden in the formatted schedule. */
  endDateHidden?: boolean;
  /** Whether time zone is displayed in formatted schedule. */
  showTimeZone?: boolean;
  /** Event recurrences. */
  recurrences?: Recurrences;
}

export interface Recurrences {
  /**
   * Event occurrences.
   * @maxSize 1000
   */
  occurrences?: Occurrence[];
  /**
   * Recurring event category ID.
   * @readonly
   */
  categoryId?: string | null;
  /**
   * Recurrence status.
   * @readonly
   */
  status?: StatusWithLiterals;
}

export interface Occurrence {
  /** Event start timestamp. */
  startDate?: Date | null;
  /** Event end timestamp. */
  endDate?: Date | null;
  /**
   * Event time zone ID in TZ database format, e.g., `EST`, `America/Los_Angeles`.
   * @maxLength 100
   */
  timeZoneId?: string | null;
  /** Whether time zone is displayed in formatted schedule. */
  showTimeZone?: boolean;
}

export enum Status {
  /** Event occurs only once. */
  ONE_TIME = 'ONE_TIME',
  /** Event is recurring. */
  RECURRING = 'RECURRING',
  /** Marks the next upcoming occurrence of the recurring event. */
  RECURRING_NEXT = 'RECURRING_NEXT',
  /** Marks the most recent ended occurrence of the recurring event. */
  RECURRING_LAST_ENDED = 'RECURRING_LAST_ENDED',
  /** Marks the most recent canceled occurrence of the recurring event. */
  RECURRING_LAST_CANCELED = 'RECURRING_LAST_CANCELED',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'ONE_TIME'
  | 'RECURRING'
  | 'RECURRING_NEXT'
  | 'RECURRING_LAST_ENDED'
  | 'RECURRING_LAST_CANCELED';

export enum EventStatus {
  /** Event is public and scheduled to start */
  SCHEDULED = 'SCHEDULED',
  /** Event has started */
  STARTED = 'STARTED',
  /** Event has ended */
  ENDED = 'ENDED',
  /** Event was canceled */
  CANCELED = 'CANCELED',
}

/** @enumType */
export type EventStatusWithLiterals =
  | EventStatus
  | 'SCHEDULED'
  | 'STARTED'
  | 'ENDED'
  | 'CANCELED';

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface TriggerNotificationRequest
  extends TriggerNotificationRequestNotificationOneOf,
    TriggerNotificationRequestGuestsOneOf {
  /** Email notification type */
  emailNotificationType?: EmailNotificationTypeWithLiterals;
  /** Push notification type */
  pushNotificationType?: PushNotificationTypeWithLiterals;
  /** Rsvp guest info */
  rsvpGuest?: RsvpGuest;
  /** Order guest info */
  orderGuest?: OrderGuest;
  /** Event guests info */
  eventGuests?: EventGuests;
  /** Ticket guests info */
  ticketGuest?: TicketGuest;
}

/** @oneof */
export interface TriggerNotificationRequestNotificationOneOf {
  /** Email notification type */
  emailNotificationType?: EmailNotificationTypeWithLiterals;
  /** Push notification type */
  pushNotificationType?: PushNotificationTypeWithLiterals;
}

/** @oneof */
export interface TriggerNotificationRequestGuestsOneOf {
  /** Rsvp guest info */
  rsvpGuest?: RsvpGuest;
  /** Order guest info */
  orderGuest?: OrderGuest;
  /** Event guests info */
  eventGuests?: EventGuests;
  /** Ticket guests info */
  ticketGuest?: TicketGuest;
}

export enum PushNotificationType {
  /** Triggered when event is updated. */
  PUSH_EVENT_UPDATED = 'PUSH_EVENT_UPDATED',
  /** Triggered when event is canceled. */
  PUSH_EVENT_CANCELATION = 'PUSH_EVENT_CANCELATION',
  /** Triggered before the event start time. */
  PUSH_UPCOMING_EVENT_REMINDER = 'PUSH_UPCOMING_EVENT_REMINDER',
  /** Triggered when event is created. */
  PUSH_EVENT_CREATED = 'PUSH_EVENT_CREATED',
}

/** @enumType */
export type PushNotificationTypeWithLiterals =
  | PushNotificationType
  | 'PUSH_EVENT_UPDATED'
  | 'PUSH_EVENT_CANCELATION'
  | 'PUSH_UPCOMING_EVENT_REMINDER'
  | 'PUSH_EVENT_CREATED';

/** Single guest associated to the RSVP */
export interface RsvpGuest {
  /**
   * Event id
   * @format GUID
   */
  eventId?: string;
  /**
   * Rsvp id
   * @format GUID
   */
  rsvpId?: string;
}

/** Single guest associated to the Order */
export interface OrderGuest {
  /**
   * Event id
   * @format GUID
   */
  eventId?: string;
  /**
   * Order number
   * @maxLength 36
   */
  orderNumber?: string;
}

/** All event guest from RSVPs and Orders */
export interface EventGuests {
  /**
   * Event id
   * @format GUID
   */
  eventId?: string;
}

/** Multiple guests associated to Tickets */
export interface TicketGuest {
  /**
   * Event id
   * @format GUID
   */
  eventId?: string;
  /**
   * Order number
   * @maxLength 36
   */
  orderNumber?: string | null;
  /**
   * Ticket numbers must be from the same order only.
   * @minSize 1
   * @maxSize 100
   * @maxLength 36
   */
  ticketNumber?: string[];
}

export interface TriggerNotificationResponse {}

/** Triggered when an order is confirmed. */
export interface OrderConfirmed {
  /** Date and time the order was confirmed. */
  timestamp?: Date | null;
  /**
   * Site language when the order was initiated.
   * @format LANGUAGE
   */
  language?: string | null;
  /** Notifications silenced for this domain event. */
  silent?: boolean | null;
  /**
   * Locale in which the order was created.
   * @format LANGUAGE_TAG
   */
  locale?: string | null;
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId?: string;
  /** Unique order number. */
  orderNumber?: string;
  /** Contact ID associated with the order. */
  contactId?: string;
  /**
   * Member ID associated with the order.
   * @format GUID
   */
  memberId?: string | null;
  /**
   * Date and time the order was created.
   * @readonly
   */
  created?: Date | null;
  /**
   * Date and time the order was updated.
   * @readonly
   */
  updated?: Date | null;
  /** Buyer first name. */
  firstName?: string;
  /** Buyer last name. */
  lastName?: string;
  /** Buyer email address. */
  email?: string;
  /** Checkout form response. */
  checkoutForm?: FormResponse;
  /** Order status. */
  status?: OrderStatusWithLiterals;
  /** Payment method used for paid tickets purchase, i.e. "payPal", "creditCard", etc. */
  method?: string | null;
  /** Tickets (generated after payment). */
  tickets?: Ticket[];
  /** Invoice. */
  invoice?: Invoice;
  /** Reservation ID associated with the order. */
  reservationId?: string;
}

export interface FormResponse {
  /**
   * Form field inputs.
   * @maxSize 200
   */
  inputValues?: InputValue[];
}

export interface InputValue {
  /**
   * Form field input name.
   * @maxLength 100
   */
  inputName?: string;
  /**
   * Form field value.
   * @maxLength 5000
   */
  value?: string;
  /**
   * Form field values.
   * @maxSize 100
   * @maxLength 5000
   */
  values?: string[];
}

export interface FormattedAddress {
  /**
   * 1-line address representation.
   * @maxLength 200
   */
  formatted?: string;
  /** Address components. */
  address?: CommonAddress;
}

/** Physical address */
export interface CommonAddress extends CommonAddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: CommonStreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine1?: string | null;
  /**
   * Country code.
   * @format COUNTRY
   */
  country?: string | null;
  /** Subdivision shorthand. Usually, a short code (2 or 3 letters) that represents a state, region, prefecture, or province. e.g. NY */
  subdivision?: string | null;
  /** City name. */
  city?: string | null;
  /** Zip/postal code. */
  postalCode?: string | null;
  /** Free text providing more detailed address info. Usually contains Apt, Suite, and Floor. */
  addressLine2?: string | null;
}

/** @oneof */
export interface CommonAddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: CommonStreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine?: string | null;
}

export interface CommonStreetAddress {
  /** Street number. */
  number?: string;
  /** Street name. */
  name?: string;
}

export interface CommonAddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface CommonSubdivision {
  /** Short subdivision code. */
  code?: string;
  /** Subdivision full name. */
  name?: string;
}

/** Subdivision Concordance values */
export interface StandardDetails {
  /**
   * subdivision iso-3166-2 code according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2). e.g. US-NY, GB-SCT, NO-30
   * @maxLength 20
   */
  iso31662?: string | null;
}

export enum OrderStatus {
  /** Order status isn't available for this request fieldset. */
  NA_ORDER_STATUS = 'NA_ORDER_STATUS',
  /** Order is confirmed, no payment is required. */
  FREE = 'FREE',
  /** Order was paid, but the payment gateway suspended the payment. */
  PENDING = 'PENDING',
  /** Order is paid. */
  PAID = 'PAID',
  /** Order is confirmed but must be paid via offline payment. Status needs to be manually updated to `"PAID"`. */
  OFFLINE_PENDING = 'OFFLINE_PENDING',
  /** Order is waiting for payment at the cashier. */
  INITIATED = 'INITIATED',
  /** Order is canceled. */
  CANCELED = 'CANCELED',
  /** Order payment is declined. */
  DECLINED = 'DECLINED',
  /** Order payment is authorized. */
  AUTHORIZED = 'AUTHORIZED',
  /** Order payment is voided. */
  VOIDED = 'VOIDED',
  /** Order is partially paid with less than the total amount. */
  PARTIALLY_PAID = 'PARTIALLY_PAID',
}

/** @enumType */
export type OrderStatusWithLiterals =
  | OrderStatus
  | 'NA_ORDER_STATUS'
  | 'FREE'
  | 'PENDING'
  | 'PAID'
  | 'OFFLINE_PENDING'
  | 'INITIATED'
  | 'CANCELED'
  | 'DECLINED'
  | 'AUTHORIZED'
  | 'VOIDED'
  | 'PARTIALLY_PAID';

export interface Ticket {
  /** Unique issued ticket number. */
  ticketNumber?: string;
  /**
   * Ticket definition ID.
   * @format GUID
   */
  ticketDefinitionId?: string;
  /** Ticket check-in. */
  checkIn?: CheckIn;
  /** Ticket price. */
  price?: Money;
  /** Whether ticket is archived. */
  archived?: boolean;
  /** Guest first name. */
  firstName?: string | null;
  /** Guest last name. */
  lastName?: string | null;
  /** Guest email. */
  email?: string | null;
  /**
   * Contact ID associated with this ticket.
   * @format GUID
   */
  contactId?: string | null;
  /** Whether ticket is confirmed */
  confirmed?: boolean;
  /**
   * Member ID associated with this ticket.
   * @format GUID
   */
  memberId?: string | null;
  /** Ticket form response (only assigned tickets contain separate forms). */
  form?: FormResponse;
  /** Ticket name. */
  ticketName?: string;
  /** Anonymized tickets no longer contain personally identifiable information (PII). */
  anonymized?: boolean;
  /** URL and password to online conference */
  onlineConferencingLogin?: OnlineConferencingLogin;
  /**
   * Seat ID associated with this ticket.
   * @maxLength 36
   */
  seatId?: string | null;
  /** Whether ticket is canceled. */
  canceled?: boolean | null;
}

export interface CheckIn {
  /** Time of a ticket's check-in. */
  created?: Date | null;
}

export interface Money {
  /**
   * *Deprecated:** Use `value` instead.
   * @format DECIMAL_VALUE
   * @deprecated
   */
  amount?: string;
  /**
   * 3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.
   * @format CURRENCY
   */
  currency?: string;
  /**
   * Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.
   * @format DECIMAL_VALUE
   */
  value?: string | null;
}

export interface OnlineConferencingLogin {
  /**
   * Link URL to the online conference.
   * @format WEB_URL
   * @readonly
   */
  link?: string;
  /**
   * Password for the online conference.
   * @readonly
   */
  password?: string | null;
}

export interface Invoice {
  items?: Item[];
  /**
   * Total cart amount.
   * @deprecated
   */
  total?: Money;
  /** Discount applied to a cart. */
  discount?: Discount;
  /** Tax applied to a cart. */
  tax?: Tax;
  /** Total cart amount before discount, tax, and fees. */
  subTotal?: Money;
  /**
   * Total amount of a cart after discount, tax, and fees.
   * Grand total is calculated in the following order:
   * 1. Total prices of all items in the cart are calculated.
   * 2. Discount is subtracted from the cart, if applicable.
   * 3. Tax is added, if applicable.
   * 4. Wix ticket service fee is added.
   */
  grandTotal?: Money;
  /**
   * Fees applied to the cart.
   * @readonly
   */
  fees?: Fee[];
  /** Total revenue, excluding fees. Taxes and payment provider fees aren't deducted. */
  revenue?: Money;
  /** Invoice preview URL. Only returned if the order is paid. */
  previewUrl?: string | null;
}

export interface Item {
  /**
   * Unique line item ID.
   * @format GUID
   */
  _id?: string;
  /**
   * Line item quantity.
   * @min 1
   * @max 50
   */
  quantity?: number;
  /** Line item name. */
  name?: string;
  /** Line item price. */
  price?: Money;
  /** Total price for line items. Always equal to price * quantity. */
  total?: Money;
  /** Discount applied to the line item. */
  discount?: Discount;
  /** Tax applied to the item. */
  tax?: Tax;
  /**
   * Fees applied to the item.
   * @readonly
   */
  fees?: Fee[];
}

export interface Discount {
  /** Total discount amount. */
  amount?: Money;
  /** Total sum after the discount. */
  afterDiscount?: Money;
  /**
   * Discount coupon code.
   * @deprecated
   */
  code?: string;
  /**
   * Discount coupon name.
   * @deprecated
   */
  name?: string;
  /**
   * Discount coupon ID.
   * @deprecated
   */
  couponId?: string;
  /** Discount items. */
  discounts?: DiscountItem[];
}

export interface DiscountItem extends DiscountItemDiscountOneOf {
  /** Coupon discount. */
  coupon?: CouponDiscount;
  /** Pricing plan discount. */
  paidPlan?: PaidPlanDiscount;
  /** Total discount amount. */
  amount?: Money;
}

/** @oneof */
export interface DiscountItemDiscountOneOf {
  /** Coupon discount. */
  coupon?: CouponDiscount;
  /** Pricing plan discount. */
  paidPlan?: PaidPlanDiscount;
}

export interface CouponDiscount {
  /** Discount coupon name. **Deprecated:** Use `invoice.discounts.coupon.name` instead. */
  name?: string;
  /** Discount coupon code. **Deprecated:** Use `invoice.discounts.coupon.code` instead. */
  code?: string;
  /** Discount coupon ID. **Deprecated:** Use `invoice.discounts.coupon.couponId` instead. */
  couponId?: string;
}

export interface PaidPlanDiscount extends PaidPlanDiscountDiscountOneOf {
  /** Discount by percentage applied to tickets. */
  percentDiscount?: PercentDiscount;
  /** Name of pricing plan. */
  name?: string;
}

/** @oneof */
export interface PaidPlanDiscountDiscountOneOf {
  /** Discount by percentage applied to tickets. */
  percentDiscount?: PercentDiscount;
}

export interface PercentDiscount {
  /**
   * Percent rate.
   * @decimalValue options { gte:0.01, lte:100, maxScale:2 }
   */
  rate?: string;
  /** Number of discounted tickets. */
  quantityDiscounted?: number;
}

export interface Tax {
  /** Tax type. */
  type?: TaxTypeWithLiterals;
  /**
   * Tax name.
   * @readonly
   */
  name?: string;
  /**
   * Tax rate.
   * @format DECIMAL_VALUE
   */
  rate?: string;
  /** Taxable amount. */
  taxable?: Money;
  /** Total tax amount. */
  amount?: Money;
}

export enum TaxType {
  /** Tax is included in the ticket price. */
  INCLUDED = 'INCLUDED',
  /** Tax is added to the order at the checkout. */
  ADDED = 'ADDED',
  /** Tax is added to the final total at the checkout. */
  ADDED_AT_CHECKOUT = 'ADDED_AT_CHECKOUT',
}

/** @enumType */
export type TaxTypeWithLiterals =
  | TaxType
  | 'INCLUDED'
  | 'ADDED'
  | 'ADDED_AT_CHECKOUT';

export interface Fee {
  /** Fee identifier. */
  name?: FeeNameWithLiterals;
  /** How fee is calculated. */
  type?: FeeTypeWithLiterals;
  /**
   * Fee rate.
   * @format DECIMAL_VALUE
   * @readonly
   */
  rate?: string;
  /** Total amount of fee charges. */
  amount?: Money;
}

export enum FeeName {
  /** Wix ticket service fee charges applied to the line item. */
  WIX_FEE = 'WIX_FEE',
}

/** @enumType */
export type FeeNameWithLiterals = FeeName | 'WIX_FEE';

export enum FeeType {
  /** Fee is added to the ticket price at checkout. */
  FEE_ADDED = 'FEE_ADDED',
  /** Seller absorbs the fee. It's deducted from the ticket price. */
  FEE_INCLUDED = 'FEE_INCLUDED',
  /** Fee is added to the ticket price at checkout. */
  FEE_ADDED_AT_CHECKOUT = 'FEE_ADDED_AT_CHECKOUT',
}

/** @enumType */
export type FeeTypeWithLiterals =
  | FeeType
  | 'FEE_ADDED'
  | 'FEE_INCLUDED'
  | 'FEE_ADDED_AT_CHECKOUT';

/** Produced if a buyer email was added to the existing order */
export interface OrderEmailAdded {
  /** Order updated timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Site language when Order initiated
   * @format LANGUAGE
   */
  language?: string | null;
  /**
   * Locale in which Order was created.
   * @format LANGUAGE_TAG
   */
  locale?: string | null;
  /** Notifications silenced for this domain event. */
  silent?: boolean | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /**
   * Unique order number.
   * @maxLength 36
   */
  orderNumber?: string;
  /**
   * Contact ID associated with this order.
   * @format GUID
   */
  contactId?: string | null;
  /**
   * Member ID associated with this order.
   * @format GUID
   */
  memberId?: string | null;
  /**
   * Order created timestamp.
   * @readonly
   */
  created?: Date | null;
  /**
   * Order updated timestamp.
   * @readonly
   */
  updated?: Date | null;
  /**
   * Buyer first name.
   * @maxLength 255
   */
  firstName?: string | null;
  /**
   * Buyer last name.
   * @maxLength 255
   */
  lastName?: string | null;
  /**
   * Buyer email.
   * @format EMAIL
   */
  email?: string | null;
  /** Checkout form response. */
  checkoutForm?: FormResponse;
  /** Whether order is confirmed - occurs once payment gateway processes the payment and funds reach merchant's account. */
  confirmed?: boolean;
  /** Order status. */
  status?: OrderStatusWithLiterals;
  /**
   * Payment method used for paid tickets purchase, i.e. "payPal", "creditCard", etc.
   * @maxLength 100
   */
  method?: string | null;
  /**
   * Tickets generated after payment.
   * @maxSize 50
   */
  tickets?: Ticket[];
  /** Whether order was archived and excluded from results. */
  archived?: boolean;
  /**
   * Reservation ID associated with this order.
   * @format GUID
   */
  reservationId?: string;
  /** Whether marketing consent was given. */
  marketingConsent?: boolean | null;
}

/** Triggered when an order is paid. */
export interface OrderPaid {
  /** Date and time the order was paid. */
  timestamp?: Date | null;
  /**
   * Site language when the order was initiated.
   * @format LANGUAGE
   */
  language?: string | null;
  /** Notifications silenced for this domain event. */
  silent?: boolean | null;
  /**
   * Locale in which the order was created.
   * @format LANGUAGE_TAG
   */
  locale?: string | null;
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId?: string;
  /** Unique order number. */
  orderNumber?: string;
  /** Reservation ID associated with this order. */
  reservationId?: string;
  /**
   * Contact ID associated with this order.
   * @maxLength 36
   */
  contactId?: string;
  /**
   * Member ID associated with this order.
   * @format GUID
   */
  memberId?: string | null;
  /**
   * Date and time the order was created.
   * @readonly
   */
  created?: Date | null;
  /**
   * Buyer first name.
   * @maxLength 255
   */
  firstName?: string;
  /**
   * Buyer last name.
   * @maxLength 255
   */
  lastName?: string;
  /**
   * Buyer email address.
   * @maxLength 255
   */
  email?: string;
  /** Checkout form response. */
  checkoutForm?: FormResponse;
  /** Order status. */
  status?: OrderStatusWithLiterals;
  /**
   * Payment method used for paid tickets purchase, i.e. "payPal", "creditCard", etc.
   * @maxLength 255
   */
  method?: string | null;
  /**
   * Tickets (generated after payment).
   * @maxSize 50
   */
  tickets?: Ticket[];
  /** Invoice. */
  invoice?: Invoice;
}

export interface TicketPdfResolved {
  /**
   * Optional order number
   * @maxLength 36
   */
  orderNumber?: string | null;
  /**
   * Optional ticket number
   * @maxLength 36
   */
  ticketNumber?: string | null;
  /**
   * Resolved download url
   * @format WEB_URL
   */
  downloadUrl?: string | null;
  /** Resolve status */
  resolveFailed?: boolean | null;
  /**
   * Papyrus document id
   * @format GUID
   */
  documentId?: string | null;
}

export interface TicketPdfResolveDelayed {
  /**
   * Optional order number
   * @maxLength 36
   */
  orderNumber?: string | null;
  /**
   * Optional ticket number
   * @maxLength 36
   */
  ticketNumber?: string | null;
  /**
   * Papyrus document id
   * @format GUID
   */
  documentId?: string | null;
}

/** Triggered when an order is canceled. */
export interface OrderCanceled {
  /**
   * Event ID to which the order belongs.
   * @format GUID
   */
  eventId?: string;
  /**
   * Unique order number.
   * @maxLength 36
   */
  orderNumber?: string;
  /**
   * Reservation ID associated with the order.
   * @format GUID
   */
  reservationId?: string;
  /**
   * Contact ID associated with the order.
   * @maxLength 36
   */
  contactId?: string;
  /**
   * Buyer first name.
   * @maxLength 50
   */
  firstName?: string | null;
  /**
   * Buyer last name.
   * @maxLength 50
   */
  lastName?: string | null;
  /**
   * Buyer email address.
   * @maxLength 255
   */
  email?: string | null;
  /** Checkout form response. */
  checkoutForm?: FormResponse;
  /** Invoice. */
  invoice?: Invoice;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface NotificationConfigCreatedEnvelope {
  entity: NotificationConfig;
  metadata: EventMetadata;
}

/** @permissionScope Read Events - all read permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.READ-EVENTS
 * @permissionScope Manage Events - all permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.MANAGE-EVENTS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Read Events
 * @permissionScopeId SCOPE.DC-EVENTS.READ-EVENTS
 * @permissionScope Manage Guest List
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-GUEST-LIST
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-EVENTS
 * @permissionId WIX_EVENTS.READ_NOTIFICATION_CONFIG
 * @webhook
 * @eventType wix.events.notifications.v2.notification_config_created
 * @serviceIdentifier wix.events.notifications.v2.NotificationConfigManagement
 * @slug created
 * @documentationMaturity preview
 */
export declare function onNotificationConfigCreated(
  handler: (event: NotificationConfigCreatedEnvelope) => void | Promise<void>
): void;

export interface NotificationConfigUpdatedEnvelope {
  entity: NotificationConfig;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/** @permissionScope Read Events - all read permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.READ-EVENTS
 * @permissionScope Manage Events - all permissions
 * @permissionScopeId SCOPE.DC-EVENTS-MEGA.MANAGE-EVENTS
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Read Events
 * @permissionScopeId SCOPE.DC-EVENTS.READ-EVENTS
 * @permissionScope Manage Guest List
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-GUEST-LIST
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.DC-EVENTS.MANAGE-EVENTS
 * @permissionId WIX_EVENTS.READ_NOTIFICATION_CONFIG
 * @webhook
 * @eventType wix.events.notifications.v2.notification_config_updated
 * @serviceIdentifier wix.events.notifications.v2.NotificationConfigManagement
 * @slug updated
 * @documentationMaturity preview
 */
export declare function onNotificationConfigUpdated(
  handler: (event: NotificationConfigUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Resolves a NotificationConfig by id. Returns saved value or default value if not saved yet.
 * @param notificationConfigId - Id of the NotificationConfig to retrieve
 * @public
 * @documentationMaturity preview
 * @requiredField notificationConfigId
 * @permissionId WIX_EVENTS.READ_NOTIFICATION_CONFIG
 * @applicableIdentity APP
 * @fqn wix.events.notifications.v2.NotificationConfigManagement.ResolveNotificationConfig
 */
export async function resolveNotificationConfig(
  notificationConfigId: string
): Promise<
  NonNullablePaths<
    ResolveNotificationConfigResponse,
    | `notificationConfig.rsvpConfirmation.notificationType`
    | `notificationConfig.rsvpConfirmation.reminderTime`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    notificationConfigId: notificationConfigId,
  });

  const reqOpts =
    ambassadorWixEventsNotificationsV2NotificationConfig.resolveNotificationConfig(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { notificationConfigId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['notificationConfigId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Upsert a NotificationConfig
 * @param _id - Event ID.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField notificationConfig
 * @permissionId WIX_EVENTS.UPDATE_NOTIFICATION_CONFIG
 * @applicableIdentity APP
 * @fqn wix.events.notifications.v2.NotificationConfigManagement.UpsertNotificationConfig
 */
export async function upsertNotificationConfig(
  _id: string,
  notificationConfig: UpsertNotificationConfig
): Promise<
  NonNullablePaths<
    UpsertNotificationConfigResponse,
    | `notificationConfig.rsvpConfirmation.notificationType`
    | `notificationConfig.rsvpConfirmation.reminderTime`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    notificationConfig: { ...notificationConfig, id: _id },
  });

  const reqOpts =
    ambassadorWixEventsNotificationsV2NotificationConfig.upsertNotificationConfig(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { notificationConfig: '$[1]' },
        explicitPathsToArguments: { 'notificationConfig.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'notificationConfig']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpsertNotificationConfig {
  /**
   * Event ID.
   * @format GUID
   * @immutable
   */
  _id?: string | null;
  /**
   * Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision.
   * @immutable
   */
  revision?: string | null;
  /**
   * Represents the time this NotificationConfig was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Represents the time this NotificationConfig was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /** Rsvp confirmation. */
  rsvpConfirmation?: EmailNotificationConfig;
  /** New spots available. */
  newSpotsAvailable?: EmailNotificationConfig;
  /** Event cancelation. */
  eventCancelation?: EmailNotificationConfig;
  /** Event reminder. */
  upcomingEventReminder?: EmailNotificationConfig;
  /** Order confirmation. */
  orderConfirmation?: EmailNotificationConfig;
  /** Ticket confirmation. */
  ticketConfirmation?: EmailNotificationConfig;
  /** Invoice. */
  invoice?: EmailNotificationConfig;
  /** Order confirmation with tickets link. */
  orderConfirmationWithTicketsLink?: EmailNotificationConfig;
}

/**
 * Triggers notification
 * @public
 * @documentationMaturity preview
 * @requiredField options
 * @requiredField options.guests
 * @requiredField options.notification
 * @permissionId WIX_EVENTS.MANAGE
 * @applicableIdentity APP
 * @fqn wix.events.notifications.v2.NotificationService.TriggerNotification
 */
export async function triggerNotification(
  options: TriggerNotificationOptions
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    emailNotificationType: options?.emailNotificationType,
    pushNotificationType: options?.pushNotificationType,
    rsvpGuest: options?.rsvpGuest,
    orderGuest: options?.orderGuest,
    eventGuests: options?.eventGuests,
    ticketGuest: options?.ticketGuest,
  });

  const reqOpts =
    ambassadorWixEventsNotificationsV2NotificationConfig.triggerNotification(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          emailNotificationType: '$[0].emailNotificationType',
          pushNotificationType: '$[0].pushNotificationType',
          rsvpGuest: '$[0].rsvpGuest',
          orderGuest: '$[0].orderGuest',
          eventGuests: '$[0].eventGuests',
          ticketGuest: '$[0].ticketGuest',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface TriggerNotificationOptions
  extends TriggerNotificationOptionsNotificationOneOf,
    TriggerNotificationOptionsGuestsOneOf {
  /** Email notification type */
  emailNotificationType?: EmailNotificationTypeWithLiterals;
  /** Push notification type */
  pushNotificationType?: PushNotificationTypeWithLiterals;
  /** Rsvp guest info */
  rsvpGuest?: RsvpGuest;
  /** Order guest info */
  orderGuest?: OrderGuest;
  /** Event guests info */
  eventGuests?: EventGuests;
  /** Ticket guests info */
  ticketGuest?: TicketGuest;
}

/** @oneof */
export interface TriggerNotificationOptionsNotificationOneOf {
  /** Email notification type */
  emailNotificationType?: EmailNotificationTypeWithLiterals;
  /** Push notification type */
  pushNotificationType?: PushNotificationTypeWithLiterals;
}

/** @oneof */
export interface TriggerNotificationOptionsGuestsOneOf {
  /** Rsvp guest info */
  rsvpGuest?: RsvpGuest;
  /** Order guest info */
  orderGuest?: OrderGuest;
  /** Event guests info */
  eventGuests?: EventGuests;
  /** Ticket guests info */
  ticketGuest?: TicketGuest;
}
